/**
 * Developer Log Viewer Portal
 * 
 * Comprehensive log viewing interface with:
 * - Real-time log streaming
 * - Search and filtering
 * - Log level filtering
 * - Export functionality
 * - Stack trace viewing
 */

import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Search,
  Download,
  Trash2,
  AlertCircle,
  Info,
  AlertTriangle,
  Bug,
  FileText,
  RefreshCw,
  Copy,
  Check,
  Eye,
  X,
} from 'lucide-react'
import { logCollector, type LogEntry, type LogLevel } from '@/utils/log-collector'
import { toast } from 'sonner'

export function DevLogsPortal() {
  const [logs, setLogs] = React.useState<LogEntry[]>([])
  const [searchQuery, setSearchQuery] = React.useState('')
  const [levelFilter, setLevelFilter] = React.useState<'all' | LogLevel>('all')
  const [autoScroll, setAutoScroll] = React.useState(true)
  const [selectedLog, setSelectedLog] = React.useState<LogEntry | null>(null)
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Subscribe to log updates
  React.useEffect(() => {
    setLogs(logCollector.getLogs())
    
    const unsubscribe = logCollector.subscribe((newLogs) => {
      setLogs(newLogs)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  // Auto-scroll to bottom when new logs arrive
  React.useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [logs, autoScroll])

  // Filter logs
  const filteredLogs = React.useMemo(() => {
    return logs.filter(log => {
      // Level filter
      if (levelFilter !== 'all' && log.level !== levelFilter) return false
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          log.message.toLowerCase().includes(query) ||
          log.level.toLowerCase().includes(query) ||
          log.timestamp.toISOString().includes(query)
        )
      }
      
      return true
    })
  }, [logs, searchQuery, levelFilter])

  const stats = logCollector.getStats()

  const handleClearLogs = () => {
    if (confirm('Clear all logs?')) {
      logCollector.clearLogs()
      toast.success('Logs cleared')
    }
  }

  const handleDownloadLogs = () => {
    logCollector.downloadLogs()
    toast.success('Logs downloaded')
  }

  const handleCopyLog = (log: LogEntry) => {
    const text = `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}] ${log.message}`
    navigator.clipboard.writeText(text)
    toast.success('Log copied to clipboard')
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Developer Logs Portal</h2>
          <p className="text-sm text-gray-600">
            Real-time application logs for debugging
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {stats.total} total logs
          </Badge>
          <Badge 
            variant={stats.enabled ? 'default' : 'secondary'} 
            className="text-xs"
          >
            {stats.enabled ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard
          label="Total"
          value={stats.total}
          icon={FileText}
          color="bg-gray-100 text-gray-700"
        />
        <StatCard
          label="Errors"
          value={stats.byLevel.error || 0}
          icon={AlertCircle}
          color="bg-red-100 text-red-700"
        />
        <StatCard
          label="Warnings"
          value={stats.byLevel.warn || 0}
          icon={AlertTriangle}
          color="bg-amber-100 text-amber-700"
        />
        <StatCard
          label="Info"
          value={stats.byLevel.info || 0}
          icon={Info}
          color="bg-blue-100 text-blue-700"
        />
        <StatCard
          label="Debug"
          value={stats.byLevel.debug || 0}
          icon={Bug}
          color="bg-purple-100 text-purple-700"
        />
      </div>

      {/* Controls */}
      <Card className="rounded-3xl bg-white/90 backdrop-blur">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search logs..."
                className="pl-9 rounded-xl"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            {/* Level Filter */}
            <Select value={levelFilter} onValueChange={(v) => setLevelFilter(v as any)}>
              <SelectTrigger className="w-full md:w-[140px] rounded-xl">
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warn">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="log">Log</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
              </SelectContent>
            </Select>

            {/* Auto-scroll toggle */}
            <Button
              variant={autoScroll ? 'default' : 'outline'}
              onClick={() => setAutoScroll(!autoScroll)}
              className="rounded-xl"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${autoScroll ? 'animate-spin' : ''}`} />
              Auto-scroll
            </Button>

            {/* Download */}
            <Button
              variant="outline"
              onClick={handleDownloadLogs}
              className="rounded-xl"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>

            {/* Clear */}
            <Button
              variant="outline"
              onClick={handleClearLogs}
              className="rounded-xl text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Display */}
      <Card className="rounded-3xl bg-white/90 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg">
            Logs {filteredLogs.length !== stats.total && `(${filteredLogs.length} filtered)`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea 
            ref={scrollRef}
            className="h-[600px] w-full"
          >
            <div className="p-4 space-y-1 font-mono text-xs">
              {filteredLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {logs.length === 0 ? (
                    <>
                      <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                      <p>No logs yet. Start using the app to see logs here.</p>
                    </>
                  ) : (
                    <>
                      <Search className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                      <p>No logs match your filters.</p>
                    </>
                  )}
                </div>
              ) : (
                filteredLogs.map((log) => (
                  <LogRow
                    key={log.id}
                    log={log}
                    onCopy={handleCopyLog}
                    onView={setSelectedLog}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Log Detail Dialog */}
      {selectedLog && (
        <LogDetailDialog
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number
  icon: any
  color: string
}

function StatCard({ label, value, icon: Icon, color }: StatCardProps) {
  return (
    <Card className={`rounded-2xl ${color} border-0`}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="h-5 w-5" />
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs font-medium opacity-80">{label}</div>
        </div>
      </CardContent>
    </Card>
  )
}

interface LogRowProps {
  log: LogEntry
  onCopy: (log: LogEntry) => void
  onView: (log: LogEntry) => void
}

function LogRow({ log, onCopy, onView }: LogRowProps) {
  const [copied, setCopied] = React.useState(false)

  const handleCopy = () => {
    onCopy(log)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const levelColors = {
    error: 'bg-red-100 text-red-700 border-red-300',
    warn: 'bg-amber-100 text-amber-700 border-amber-300',
    info: 'bg-blue-100 text-blue-700 border-blue-300',
    log: 'bg-gray-100 text-gray-700 border-gray-300',
    debug: 'bg-purple-100 text-purple-700 border-purple-300',
  }

  const levelIcons = {
    error: AlertCircle,
    warn: AlertTriangle,
    info: Info,
    log: FileText,
    debug: Bug,
  }

  const Icon = levelIcons[log.level]

  return (
    <div 
      className={`
        group p-2 rounded-lg border hover:shadow-sm transition-all cursor-pointer
        ${levelColors[log.level]}
      `}
      onClick={() => onView(log)}
    >
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] opacity-60">
              {log.timestamp.toLocaleTimeString()}.{log.timestamp.getMilliseconds()}
            </span>
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              {log.level.toUpperCase()}
            </Badge>
          </div>
          <div className="text-xs break-words line-clamp-2">
            {log.message}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleCopy()
            }}
            className="p-1 hover:bg-white/50 rounded"
            title="Copy log"
          >
            {copied ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onView(log)
            }}
            className="p-1 hover:bg-white/50 rounded"
            title="View details"
          >
            <Eye className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  )
}

interface LogDetailDialogProps {
  log: LogEntry
  onClose: () => void
}

function LogDetailDialog({ log, onClose }: LogDetailDialogProps) {
  const [copied, setCopied] = React.useState(false)

  const handleCopy = () => {
    const text = JSON.stringify(log, null, 2)
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Log Details</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="rounded-xl"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
          <DialogDescription>
            {log.timestamp.toLocaleString()} - {log.level.toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4">
            {/* Message */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Message</h4>
              <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                {log.message}
              </pre>
            </div>

            {/* Arguments */}
            {log.args.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Arguments</h4>
                <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                  {JSON.stringify(log.args, null, 2)}
                </pre>
              </div>
            )}

            {/* Stack Trace */}
            {log.stack && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Stack Trace</h4>
                <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto font-mono">
                  {log.stack}
                </pre>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Metadata</h4>
              <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-1">
                <div><strong>ID:</strong> {log.id}</div>
                <div><strong>Timestamp:</strong> {log.timestamp.toISOString()}</div>
                <div><strong>Level:</strong> {log.level}</div>
                {log.url && <div><strong>URL:</strong> {log.url}</div>}
                {log.lineNumber && <div><strong>Line:</strong> {log.lineNumber}:{log.columnNumber}</div>}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
